import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PropertyCard from '@/components/PropertyCard';
import Seo from '@/components/Seo';
import { getProperties, toggleSaveProperty, getSavedProperties } from '@/lib/api';
import { loadGoogleMaps } from '@/lib/googleMaps';
import { shortPrice, formatPrice, localityOf } from '@/lib/format';
import { useAuth } from '@/contexts/AuthContext';
import { useLocationCtx } from '@/contexts/LocationContext';
import { toast } from 'sonner';
import { Loader2, List, Map as MapIcon, X, LocateFixed, SlidersHorizontal } from 'lucide-react';

const CITY_CENTERS = { Mumbai: [19.076, 72.8777], Pune: [18.5204, 73.8567], Bangalore: [12.9716, 77.5946], Gurgaon: [28.4595, 77.0266], Delhi: [28.6139, 77.209], Noida: [28.5355, 77.391], Hyderabad: [17.385, 78.4867], Ahmedabad: [23.0225, 72.5714], Chennai: [13.0827, 80.2707], Kolkata: [22.5726, 88.3639], Indore: [22.7196, 75.8577], Faridabad: [28.4089, 77.3178], 'Navi Mumbai': [19.033, 73.0297] };
const INDIA = { center: { lat: 21.5, lng: 78.5 }, zoom: 5 };

const makePillClass = (maps) => class PricePill extends maps.OverlayView {
  constructor(pos, label, onClick, onHover) { super(); this.pos = pos; this.label = label; this.onClick = onClick; this.onHover = onHover; this.el = null; }
  onAdd() {
    const el = document.createElement('button');
    el.className = 'be-pill';
    el.textContent = this.label;
    el.setAttribute('data-testid', 'map-search-price-marker');
    el.addEventListener('click', (e) => { e.stopPropagation(); this.onClick(); });
    el.addEventListener('mouseenter', () => this.onHover(true));
    el.addEventListener('mouseleave', () => this.onHover(false));
    maps.OverlayView.preventMapHitsAndGesturesFrom(el);
    this.el = el;
    this.getPanes().overlayMouseTarget.appendChild(el);
  }
  draw() {
    if (!this.el) return;
    const p = this.getProjection().fromLatLngToDivPixel(new maps.LatLng(this.pos.lat, this.pos.lng));
    if (p) { this.el.style.left = `${p.x}px`; this.el.style.top = `${p.y}px`; }
  }
  onRemove() { this.el?.remove(); this.el = null; }
  setActive(on) { this.el?.classList.toggle('be-pill--active', on); if (on) this.el.style.zIndex = 20; else this.el.style.zIndex = ''; }
};

const MapSearchPage = () => {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { location } = useLocationCtx();
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const pillsRef = useRef({});
  const PillRef = useRef(null);
  const readyRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  const [mobileView, setMobileView] = useState('map');

  const filters = useMemo(() => ({
    listing_type: params.get('listing_type') || '', property_type: params.get('property_type') || '',
    min_price: params.get('min_price') || '', max_price: params.get('max_price') || '', bedrooms: params.get('bedrooms') || '',
    is_project: params.get('is_project') || '',
  }), [params]);

  const setFilter = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p); };

  const fetchInBounds = useCallback(async () => {
    const map = mapRef.current; if (!map) return;
    const b = map.getBounds(); if (!b) return;
    const sw = b.getSouthWest(), ne = b.getNorthEast();
    setLoading(true); setDirty(false);
    try {
      const data = await getProperties({ ...filters, bounds: `${sw.lat()},${sw.lng()},${ne.lat()},${ne.lng()}`, limit: 200, sort: 'newest' });
      setResults(data);
    } catch { toast.error('Could not load properties for this area'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => {
    if (user) getSavedProperties().then((s) => setSavedIds(new Set(s.map((p) => p.property_id)))).catch(() => {});
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then((maps) => {
      if (cancelled || !mapEl.current) return;
      PillRef.current = makePillClass(maps);
      const city = params.get('city') || location?.city;
      const c = CITY_CENTERS[city];
      const center = params.get('lat') ? { lat: +params.get('lat'), lng: +params.get('lng') } : c ? { lat: c[0], lng: c[1] } : location?.lat ? { lat: location.lat, lng: location.lng } : INDIA.center;
      const map = new maps.Map(mapEl.current, { center, zoom: c || params.get('lat') || location?.lat ? 12 : INDIA.zoom, mapTypeControl: false, streetViewControl: false, fullscreenControl: false, clickableIcons: false, gestureHandling: 'greedy', zoomControl: true, zoomControlOptions: { position: maps.ControlPosition.RIGHT_CENTER } });
      mapRef.current = map;
      map.addListener('click', () => setSelected(null));
      map.addListener('idle', () => { if (readyRef.current) setDirty(true); });
      maps.event.addListenerOnce(map, 'idle', () => { readyRef.current = true; setMapReady(true); });
    }).catch(() => toast.error('Google Maps failed to load'));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (mapReady) fetchInBounds(); }, [filters, mapReady, fetchInBounds]);

  useEffect(() => {
    const map = mapRef.current, Pill = PillRef.current; if (!map || !Pill) return;
    Object.values(pillsRef.current).forEach((p) => p.setMap(null));
    pillsRef.current = {};
    results.forEach((p) => {
      if (!Number.isFinite(p.latitude)) return;
      const pill = new Pill({ lat: p.latitude, lng: p.longitude }, shortPrice(p.price), () => setSelected(p), (on) => setActiveId(on ? p.property_id : null));
      pill.setMap(map);
      pillsRef.current[p.property_id] = pill;
    });
  }, [results]);

  useEffect(() => { Object.entries(pillsRef.current).forEach(([id, pill]) => pill.setActive(id === activeId || id === selected?.property_id)); }, [activeId, selected]);

  const handleSave = async (id) => {
    if (!user) return toast.error('Please login to save properties');
    const r = await toggleSaveProperty(id);
    setSavedIds((prev) => { const n = new Set(prev); r.saved ? n.add(id) : n.delete(id); return n; });
  };

  const locateMe = () => navigator.geolocation?.getCurrentPosition((p) => { mapRef.current?.panTo({ lat: p.coords.latitude, lng: p.coords.longitude }); mapRef.current?.setZoom(13); }, () => toast.error('Location permission denied'));

  const onCardHover = (id) => { setActiveId(id); };

  return (
    <div className="h-[calc(100vh-72px)] lg:h-[calc(100vh-80px)] flex flex-col bg-white" data-testid="map-search-split-container">
      <Seo title="Map Search — Properties on the Map" description="Explore properties for sale and rent across India on an interactive map with live price pins." path="/map-search" />
      <div className="border-b border-slate-200 px-4 py-2.5 flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="map-search-filter-bar">
        <SlidersHorizontal className="h-4 w-4 text-slate-400 flex-shrink-0" />
        <Select value={filters.listing_type || 'all'} onValueChange={(v) => setFilter('listing_type', v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9 w-28 rounded-full text-sm" data-testid="map-filter-listing"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Buy & Rent</SelectItem><SelectItem value="buy">Buy</SelectItem><SelectItem value="rent">Rent</SelectItem></SelectContent>
        </Select>
        <Select value={filters.property_type || 'all'} onValueChange={(v) => setFilter('property_type', v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9 w-36 rounded-full text-sm" data-testid="map-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All types</SelectItem><SelectItem value="apartment">Apartment</SelectItem><SelectItem value="villa">Villa</SelectItem><SelectItem value="house">House</SelectItem><SelectItem value="plot">Plot</SelectItem><SelectItem value="commercial">Commercial</SelectItem></SelectContent>
        </Select>
        <Select value={filters.bedrooms || 'all'} onValueChange={(v) => setFilter('bedrooms', v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9 w-28 rounded-full text-sm" data-testid="map-filter-beds"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Any BHK</SelectItem>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n} BHK</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.max_price || 'all'} onValueChange={(v) => setFilter('max_price', v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9 w-36 rounded-full text-sm" data-testid="map-filter-budget"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Any budget</SelectItem><SelectItem value="5000000">Under ₹50 Lac</SelectItem><SelectItem value="10000000">Under ₹1 Cr</SelectItem><SelectItem value="20000000">Under ₹2 Cr</SelectItem><SelectItem value="50000000">Under ₹5 Cr</SelectItem></SelectContent>
        </Select>
        <span className="ml-auto text-sm text-slate-600 whitespace-nowrap flex-shrink-0" data-testid="map-search-count">{loading ? 'Searching…' : `${results.length} propert${results.length === 1 ? 'y' : 'ies'} in view`}</span>
        <Link to={`/properties?${params.toString()}`} className="hidden md:inline-flex flex-shrink-0"><Button variant="outline" size="sm" className="h-9 rounded-full"><List className="h-4 w-4 mr-1.5" /> List view</Button></Link>
      </div>

      <div className="flex-1 flex min-h-0 relative">
        <aside className={`${mobileView === 'list' ? 'flex' : 'hidden'} lg:flex w-full lg:w-[42%] xl:w-[40%] flex-col border-r border-slate-200 bg-[#F9F9F7]`} data-testid="map-search-results-list">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {results.length === 0 && !loading && (
              <div className="text-center py-20 text-slate-500" data-testid="map-search-empty"><MapIcon className="h-10 w-10 mx-auto mb-3 text-slate-300" />No properties in this area yet.<br />Zoom out or move the map.</div>
            )}
            {results.map((p) => <PropertyCard key={p.property_id} property={p} onSave={handleSave} isSaved={savedIds.has(p.property_id)} variant="list" onHover={onCardHover} active={activeId === p.property_id || selected?.property_id === p.property_id} />)}
          </div>
        </aside>

        <div className={`${mobileView === 'map' ? 'block' : 'hidden'} lg:block flex-1 relative`}>
          <div ref={mapEl} className="absolute inset-0" data-testid="map-search-canvas" />
          {dirty && !loading && (
            <Button onClick={fetchInBounds} className="absolute top-3 left-1/2 -translate-x-1/2 h-9 rounded-full bg-[#0F172A] text-white shadow-lg hover:bg-[#1E293B]" data-testid="map-search-this-area-button">Search this area</Button>
          )}
          {loading && <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-white rounded-full px-3 py-1.5 text-sm shadow flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading</div>}
          <button onClick={locateMe} className="absolute right-3 top-3 h-10 w-10 rounded-full bg-white shadow flex items-center justify-center text-[#0F172A] hover:bg-slate-50" title="My location" data-testid="map-locate-me"><LocateFixed className="h-5 w-5" /></button>

          {selected && (
            <div className="absolute left-3 right-3 bottom-3 sm:left-auto sm:w-[340px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden" data-testid="map-search-mini-card-popover">
              <button onClick={() => setSelected(null)} className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 flex items-center justify-center z-10"><X className="h-4 w-4" /></button>
              <Link to={`/properties/${selected.property_id}`} className="flex gap-3 p-3">
                <img src={selected.images?.[0]} alt="" className="h-24 w-28 rounded-xl object-cover flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-lg font-extrabold text-[#0F172A]">{formatPrice(selected.price)}{selected.listing_type === 'rent' ? <span className="text-xs font-medium text-slate-500">/mo</span> : ''}</p>
                  <p className="text-sm font-semibold text-[#0F172A] line-clamp-1">{selected.title}</p>
                  <p className="text-xs text-slate-500 line-clamp-1">{localityOf(selected)}, {selected.city}</p>
                  <p className="text-xs text-slate-600 mt-1">{selected.bedrooms ? `${selected.bedrooms} BHK · ` : ''}{Number(selected.area).toLocaleString('en-IN')} {selected.area_unit}</p>
                  <span className="text-xs font-semibold text-[#B5952F] mt-1 inline-block">View details →</span>
                </div>
              </Link>
            </div>
          )}
        </div>

        <div className="lg:hidden absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
          <Button onClick={() => setMobileView(mobileView === 'map' ? 'list' : 'map')} className="h-11 rounded-full bg-[#0F172A] text-white shadow-xl px-5" data-testid="map-mobile-toggle">
            {mobileView === 'map' ? <><List className="h-4 w-4 mr-2" /> List ({results.length})</> : <><MapIcon className="h-4 w-4 mr-2" /> Map</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MapSearchPage;
